import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';

import { CollectionListApiResponse } from '../apis';

import { clientAxios } from '@/shared/api';
import {
  convertArticleItem,
  convertHighlightsItem,
  convertCollectionItem,
  convertCollectionListItem,
  convertCollectionScrapItem,
} from '@/shared/utils';
import {
  ApiResponse,
  ArticleResponseItem,
  CollectionSnakeCase as CollectionApiResponse,
  CollectionNewScrapsSnakeCase as CollectionNewScrapsApiResponse,
  CollectionHighlightsSnakeCase as CollectionHighlightsApiResponse,
} from '@/shared/types';

interface CollectionCurrentViewApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ArticleResponseItem[];
}

interface CollectionCurrentScrapsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: CollectionNewScrapsApiResponse[];
}

interface MyScrappedCollectionApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: CollectionApiResponse[];
}

interface MyHighlightsCollectionApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: CollectionHighlightsApiResponse[];
}

export const useCollectionCurrentViewQuery = ({ page }: { page: number }) =>
  useQuery({
    queryKey: ['collectionCurrentViewApi', page],
    queryFn: async () => {
      const { data } = await clientAxios.get<
        ApiResponse<CollectionCurrentViewApiResponse>
      >('/collectionCurrentViewApi/', {
        params: { page },
      });

      return {
        list: convertArticleItem(data.data.results),
        maxPage: Math.ceil(data.data.count / 10),
        totalCount: data.data.count,
      };
    },
  });

export const useCollectionCurrentUseQuery = () =>
  useQuery({
    queryKey: ['collectionCurrentUseApi'],
    queryFn: async () => {
      const { data } = await clientAxios.get<
        ApiResponse<CollectionListApiResponse>
      >('/collectionCurrentUseApi/');

      const collectionCurrentUses = data.data.results;

      const convertedCollectionCurrentUses = convertCollectionListItem(
        collectionCurrentUses
      );

      return convertedCollectionCurrentUses;
    },
  });

export const useCollectionNewScrapContentsQuery = () =>
  useQuery({
    queryKey: ['collectionNewScrapContentsApi'],
    queryFn: async () => {
      const { data } = await clientAxios.get<
        ApiResponse<CollectionCurrentScrapsResponse>
      >('/collectionNewScrapContentsApi/');

      return convertCollectionScrapItem(data.data.results);
    },
  });

export const useCollectionDetailQuery = ({
  id,
  affectView,
}: {
  id?: number;
  affectView?: boolean;
}) =>
  useQuery({
    enabled: !!id,
    retry: false,
    queryKey: ['collectionMyDetailApi', id],
    queryFn: async () => {
      const { data } = await clientAxios.get(`/collectionMyDetailApi/`, {
        params: { id, affectView, t: Date.now() },
      });
      return convertCollectionItem([data.data])[0];
    },
  });

export const usePatchMyCollectionPinnedMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isFixed }: { id: number; isFixed: boolean }) =>
      clientAxios.patch('/patchMyCollectionPinnedApi/', { id, isFixed }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['collectionMyDetailApi'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['getMyCollectionsApi'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['collectionCurrentUseApi'],
        }),
      ]);
    },
  });
};

export const usePatchMyCollectionStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isPublic }: { id: number; isPublic: boolean }) =>
      clientAxios.patch('/patchMyCollectionStatusApi/', { id, isPublic }),
    onSuccess: async (_, { id }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['collectionMyDetailApi'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['getMyCollectionsApi'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['recommendMyCollectionApi'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['collectionCurrentUseApi'],
        }),
      ]);
    },
  });
};

export const useMyScrappedCollectionQuery = ({ page }: { page: number }) =>
  useQuery({
    queryKey: ['collectionMyScrapApi', page],
    queryFn: async () => {
      const { data } = await clientAxios.get<
        ApiResponse<MyScrappedCollectionApiResponse>
      >(`/collectionMyScrapApi/`, { params: { page } });

      return {
        totalCount: data.data.count,
        maxPage: Math.ceil(data.data.count / 9),
        list: convertCollectionItem(data.data.results),
      };
    },
    placeholderData: keepPreviousData,
  });

export const useMyHighlightsCollectionQuery = ({ page }: { page: number }) =>
  useQuery({
    queryKey: ['getCollectionMyHighlightsApi', page],
    queryFn: async () => {
      const { data } = await clientAxios.get<
        ApiResponse<MyHighlightsCollectionApiResponse>
      >(`/getCollectionMyHighlightsApi/`, { params: { page } });

      return {
        totalCount: data.data.count,
        maxPage: Math.ceil(data.data.count / 10),
        list: convertHighlightsItem(data.data.results),
      };
    },
    placeholderData: keepPreviousData,
  });

export const usePatchMyHighlightsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, memo }: { id: number; memo: string }) =>
      clientAxios.patch('/patchMyHighlightsApi/', { id, memo }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['getCollectionMyHighlightsApi'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['getProfileIdApi'],
        }),
      ]);
    },
  });
};

export const useDeleteMyHighlightsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: number }) => {
      return clientAxios.delete('/deleteCollectionMyHighlightsApi/', {
        data: { id },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getCollectionMyHighlightsApi'],
      });
    },
  });
};

export const useDeleteMyScrapsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ queryParams }: { queryParams: string }) => {
      return clientAxios.delete('/deleteMyScrappedCollectionApi/', {
        data: { queryParams },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['collectionMyScrapApi'],
      });
      queryClient.invalidateQueries({ queryKey: ['collectionMyDetailApi'] });
    },
  });
};

export const useDeleteMyCurrentViewArticleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: number | null }) => {
      return clientAxios.delete('/deleteMyCurrentViewArticleApi/', {
        data: { id },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['collectionCurrentViewApi'],
      });
    },
  });
};

export const useDeleteScrappedArticleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      scrapId,
      collectionId,
    }: {
      scrapId: number;
      collectionId: number;
    }) => {
      return clientAxios.delete('/deleteScrappedArticleApi/', {
        data: { scrapId, collectionId },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['collectionMyDetailApi'],
      });
    },
  });
};
